import React from "react";
import { InlineMath } from "react-katex";
import { makeSimpleClosedTask, normalizeMay2026Variant } from "../../../../matura/MaturaMay2026ClosedTaskSupport";
import { getCoordinateTriangle, pointLatex } from "./MaturaMay2026CoordinateTriangleData";
import { sideLengthsAligned, wrapNegative } from "./coordinateExplanationLatex";

function sideSquared([x1, y1], [x2, y2]) {
  return (x2 - x1) ** 2 + (y2 - y1) ** 2;
}

function sqrtLatex(value) {
  const root = Math.sqrt(value);
  if (Number.isInteger(root)) return String(root);
  for (let factor = Math.floor(root); factor >= 2; factor -= 1) {
    if (value % (factor ** 2) === 0) {
      const remainder = value / (factor ** 2);
      return remainder === 1 ? String(factor) : `${factor}\\sqrt{${remainder}}`;
    }
  }
  return `\\sqrt{${value}}`;
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const data = getCoordinateTriangle(variant === "original");
  const answer = String(data.area).replace(".5", "{,}5");
  const [xA, yA] = data.A;
  const [xB, yB] = data.B;
  const [xC, yC] = data.C;
  const sides = [
    { name: "AB", from: data.A, to: data.B, squared: sideSquared(data.A, data.B) },
    { name: "AC", from: data.A, to: data.C, squared: sideSquared(data.A, data.C) },
    { name: "BC", from: data.B, to: data.C, squared: sideSquared(data.B, data.C) },
  ];
  const hypotenuse = sides.reduce((longest, side) => side.squared > longest.squared ? side : longest);
  const legs = sides.filter((side) => side.name !== hypotenuse.name);
  const lengthsLatex = sideLengthsAligned(sides);
  return makeSimpleClosedTask({
    type: "MaturaCoordinateRightTriangleAreaClosedTask",
    taskNumber: "24.1",
    variant,
    prompt: <span>Punkty <InlineMath math={`A=${pointLatex(data.A)},B=${pointLatex(data.B)},C=${pointLatex(data.C)}`}/> są wierzchołkami trójkąta prostokątnego. Jego pole jest równe:</span>,
    correct: answer,
    wrong: [String(data.area + 1), String(data.area * 2), String(Math.max(1, data.area - 2))],
    tags: ["geometria analityczna", "pole trojkata"],
    explanationSteps: [
      {
        title: "Korzystamy ze wzoru na pole trójkąta o danych współrzędnych wierzchołków z karty wzorów.",
        latex: "P_{\\triangle ABC}=\\frac12\\left|(x_B-x_A)(y_C-y_A)-(y_B-y_A)(x_C-x_A)\\right|",
      },
      {
        title: "Podstawiamy współrzędne punktów i wykonujemy działania.",
        latex: `\\begin{aligned}P_{\\triangle ABC}&=\\frac12\\left|(${wrapNegative(xB)}-${wrapNegative(xA)})(${wrapNegative(yC)}-${wrapNegative(yA)})-(${wrapNegative(yB)}-${wrapNegative(yA)})(${wrapNegative(xC)}-${wrapNegative(xA)})\\right|=\\\\&=${answer}\\end{aligned}`,
      },
    ],
    alternativeExplanation: {
      title: "Inny sposób: obliczamy długości boków",
      intro: "Ponieważ trójkąt jest prostokątny, możemy wskazać przeciwprostokątną i obliczyć pole z długości obu przyprostokątnych.",
      steps: [
        {
          title: "Obliczamy długości wszystkich boków ze wzoru na odległość punktów.",
          latex: lengthsLatex,
        },
        {
          title: <>Najdłuższym bokiem jest <InlineMath math={hypotenuse.name} />, więc to przeciwprostokątna. Boki <InlineMath math={legs[0].name} /> i <InlineMath math={legs[1].name} /> są przyprostokątnymi.</>,
          latex: `P_{\\triangle ABC}=\\frac12\\cdot ${sqrtLatex(legs[0].squared)}\\cdot ${sqrtLatex(legs[1].squared)}=${answer}`,
        },
      ],
    },
  });
}

export { checkClosedAnswer as checkAnswer, renderClosedAnswer as renderAnswer, renderClosedExplanation as renderExplanation, renderClosedInput as renderInput, renderClosedTask as renderTask, renderClosedUserAnswer as renderUserAnswer } from "../../../../matura/MaturaMay2026ClosedTaskSupport";
